<template>
  <div class="w-full pt-2">
    <BaseAnimatedExtend
      :is-extended="hasValue"
      :fixed-height="height"
      :duration="200"
      class="w-full px-2 lg:mt-4"
    >
      <div class="flex w-full flex-row pb-2 lg:justify-center">
        <AdditionalTextFeedbackEditor
          v-if="isInlineEditing"
          v-model="draftText"
          class="max-w-lg"
          :can-delete="!!draftText || !!text"
          @save="addText"
          @delete="deleteText"
          @close="isTextFieldVisible = false"
        />

        <BaseTextButton
          v-else-if="!text"
          color="outlined"
          shape="square"
          icon="comment"
          class="ease-fast-spatial w-full transition-transform delay-100 duration-200 lg:w-auto"
          :class="[hasValue && 'translate-y-0', !hasValue && 'translate-y-10']"
          @click="isTextFieldVisible = true"
          >{{ $t('common.addText') }}</BaseTextButton
        >
        <div
          v-else
          class="text-body-medium bg-secondary-container rounded-medium text-on-secondary-container flex w-full flex-row items-center p-3"
          @click="isTextFieldVisible = true"
        >
          <BaseIcon class="text-on-secondary-container mr-3 shrink-0">comment</BaseIcon>
          <span class="line-clamp-1 grow pr-2 italic">
            {{ previewText }}
          </span>
          <BaseIconButton
            v-tooltip="$t('common.editText')"
            icon="edit"
            size="XS"
            color="outlined"
          />
        </div>
      </div>
    </BaseAnimatedExtend>
    <BaseBottomSheet
      :is-open="isTextFieldVisible && !isLg"
      :title="$t('common.addText')"
      @close="isTextFieldVisible = false"
    >
      <AdditionalTextFeedbackEditor
        v-model="draftText"
        :can-delete="!!draftText || !!text"
        @save="addText"
        @delete="deleteText"
      />
    </BaseBottomSheet>
  </div>
</template>
<script lang="ts" setup>
  import AdditionalTextFeedbackEditor from './AdditionalTextFeedbackEditor.vue'
  import BaseAnimatedExtend from '@/core/components/base/animated-extend/BaseAnimatedExtend.vue'
  import BaseBottomSheet from '@/core/components/base/bottom-sheet/BaseBottomSheet.vue'
  import BaseIconButton from '@/core/components/base/button/icon-button/BaseIconButton.vue'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import BaseIcon from '@/core/components/base/icon/BaseIcon.vue'
  import { useBreakpoint } from '@/core/composables/use-break-points'
  import { useWysiwygPlainText } from '@/core/composables/use-wysiwyg-plain-text'
  import type { JSONContent } from '@tiptap/vue-3'
  import { useLocalStorage } from '@vueuse/core'
  import { computed, ref, watch } from 'vue'
  import { useRoute } from 'vue-router'

  interface Props {
    hasValue: boolean
    questionId: string
  }

  const props = defineProps<Props>()
  const isTextFieldVisible = ref(false)

  const route = useRoute()
  const surveyId = ref(route.params.surveyId as string)
  const text = defineModel<JSONContent | undefined>('text')
  const draftText = useLocalStorage<JSONContent | undefined>(
    'draftText-' + props.questionId + '-' + surveyId.value,
    text.value,
  )

  watch(text, (newVal) => {
    draftText.value = newVal
  })

  const addText = () => {
    text.value = draftText.value
    draftText.value = undefined
    isTextFieldVisible.value = false
  }

  const deleteText = () => {
    draftText.value = undefined
    text.value = undefined
    isTextFieldVisible.value = false
  }

  const { getPreview } = useWysiwygPlainText()

  const previewText = computed(() => {
    if (text) {
      return getPreview(text.value as JSON)
    }
    return undefined
  })

  const { breakpoints } = useBreakpoint()
  const isLg = breakpoints.greaterOrEqual('lg')

  const isInlineEditing = computed(() => isLg.value && isTextFieldVisible.value)

  const height = computed(() => {
    if (isInlineEditing.value) {
      return undefined
    }
    if (isLg.value) {
      return text.value ? 68 : 52
    }
    return text.value ? 68 : 52
  })
</script>
